<template>
  <div class="modal fade receipting-modal" tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content border-0 shadow">
        <!-- Modal Header -->
        <div class="modal-header border-0 py-3" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);">
          <div class="d-flex align-items-center">
            <div class="modal-icon me-3">
              <i class="bx bx-file-find text-white"></i>
            </div>
            <div>
              <h5 class="modal-title mb-0 fw-semibold text-white">Inspection Details</h5>
              <small class="text-white opacity-75">{{ application.billNo }}</small>
            </div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <span :class="getStatusClass(application.status)" class="badge rounded-pill px-3">
              {{ application.status }}
            </span>
            <button type="button" class="btn-close btn-close-white ms-2" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
        </div>

        <!-- Modal Body -->
        <div class="modal-body p-0">
          <div class="row g-0">
            <!-- Left Panel - Info -->
            <div class="col-lg-5 border-end bg-light">
              <div class="p-4">
                <!-- Business Info -->
                <div class="info-section mb-4">
                  <div class="section-title">
                    <i class="bx bx-store-alt text-primary"></i>
                    <span>Business Information</span>
                  </div>
                  <div class="info-content">
                    <div class="info-item">
                      <span class="label">Business Name</span>
                      <span class="value fw-semibold">{{ application.businessName || '-' }}</span>
                    </div>
                    <div class="info-item">
                      <span class="label">Business ID</span>
                      <span class="value">{{ application.businessID || '-' }}</span>
                    </div>
                    <div class="info-item">
                      <span class="label">Category</span>
                      <span class="value">{{ application.businessCategory || '-' }}</span>
                    </div>
                    <div class="info-item">
                      <span class="label">Sub Category</span>
                      <span class="value small" :title="application.businessSubCategory">
                        {{ truncateText(application.businessSubCategory, 30) || '-' }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Location Info -->
                <div class="info-section mb-4">
                  <div class="section-title">
                    <i class="bx bx-map text-success"></i>
                    <span>Location Details</span>
                  </div>
                  <div class="info-content">
                    <div class="info-item">
                      <span class="label">Sub County</span>
                      <span class="value">{{ application.subCountyName || '-' }}</span>
                    </div>
                    <div class="info-item">
                      <span class="label">Ward</span>
                      <span class="value">{{ application.wardName || '-' }}</span>
                    </div>
                    <div class="info-item">
                      <span class="label">Plot Number</span>
                      <span class="value">{{ application.plotNumber || '-' }}</span>
                    </div>
                  </div>
                </div>

                <!-- Invoice Info -->
                <div class="info-section">
                  <div class="section-title">
                    <i class="bx bx-receipt text-info"></i>
                    <span>Invoice Details</span>
                  </div>
                  <div class="info-content">
                    <div class="info-item">
                      <span class="label">Invoice No.</span>
                      <span class="value text-primary fw-semibold">{{ application.billNo || '-' }}</span>
                    </div>
                    <div class="info-item">
                      <span class="label">Date Invoiced</span>
                      <span class="value">{{ formatDate(application.dateOfIssue) }}</span>
                    </div>
                    <div class="info-item">
                      <span class="label">Fiscal Year</span>
                      <span class="value">{{ application.fiscalYear || '-' }}</span>
                    </div>
                    <div class="info-item highlight">
                      <span class="label">Application Fee</span>
                      <span class="value fw-bold text-success">KES {{ numberFormat(application.billTotal) || '0' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Panel - Form -->
            <div class="col-lg-7">
              <div class="p-4 modal-form-scroll">
                <div class="section-title mb-3">
                  <i class="bx bx-edit-alt text-primary"></i>
                  <span>Inspection Form</span>
                </div>

                <!-- License Selection -->
                <div class="row g-3 mb-3">
                  <div class="col-md-6">
                    <label class="form-label small fw-semibold">
                      License Duration <span class="text-danger">*</span>
                    </label>
                    <select @change="selectedLicenseDurations($event)" class="form-select"
                            :class="{ 'is-invalid': validationAttempted && !duration }">
                      <option value="">Select Duration</option>
                      <option :key="i" :value="d.duration" v-for="(d, i) in licenseDurations">{{ d.duration }}</option>
                    </select>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small fw-semibold">
                      License Category <span class="text-danger">*</span>
                    </label>
                    <select @change="getLicenceSubCategory($event)" class="form-select"
                            :class="{ 'is-invalid': validationAttempted && !selectedIncomeType?.incomeTypeId }">
                      <option value="">Select Category</option>
                      <option :key="i" :value="JSON.stringify(c)" v-for="(c, i) in licenceCategories">
                        {{ c.incomeTypeDescription }}
                      </option>
                    </select>
                  </div>
                </div>

                <div class="mb-3">
                  <label class="form-label small fw-semibold">
                    License Sub Category <span class="text-danger">*</span>
                  </label>
                  <select @change="selectedLicenceSubCategory($event)" class="form-select"
                          :class="{ 'is-invalid': validationAttempted && !selectedFeeAndCharge?.feeId }">
                    <option value="">Select Sub Category</option>
                    <option :key="i" :value="JSON.stringify(s)" v-for="(s, i) in licenceSubCategories">
                      {{ s.feeDescription }} - KES {{ numberFormat(s.unitFeeAmount) }}
                    </option>
                  </select>
                </div>

                <!-- Operational Requirements -->
                <div class="mb-3">
                  <label class="form-label small fw-semibold">
                    <i class="bx bx-check-shield me-1 text-warning"></i>Operational Requirements
                  </label>
                  <div class="requirements-box">
                    <div v-for="(req, i) in operationalRequirements" :key="i" class="form-check">
                      <input class="form-check-input" type="checkbox" :id="'req' + req.id"
                             v-model="selectedRequirements" :value="req.id">
                      <label class="form-check-label small" :for="'req' + req.id">{{ req.requirement_name }}</label>
                    </div>
                  </div>
                </div>

                <!-- Other Requirements -->
                <div class="mb-3">
                  <label class="form-label small fw-semibold">Other Requirements (Optional)</label>
                  <textarea v-model="otherRequirements" class="form-control" rows="2"
                            placeholder="Additional requirements..."></textarea>
                </div>

                <!-- ==================== OTHER APPLICABLE CHARGES SECTION ==================== -->
                <div class="other-charges-section mb-3">
                  <div class="section-title mb-3">
                    <i class="bx bx-receipt text-warning"></i>
                    <span>Other Applicable Charges</span>
                  </div>

                  <!-- Step 1: Select Charge Category -->
                  <div class="mb-3">
                    <label class="form-label small fw-semibold">
                      <span class="badge bg-primary me-1">Step 1</span> Select Charge Category
                    </label>
                    <div class="d-flex gap-2">
                      <select v-model="selectedOtherCategory" @change="handleOtherCategoryChange" class="form-select">
                        <option :value="null">-- Select a category --</option>
                        <option v-for="(category, index) in otherChargeCategories" :key="index" :value="category">
                          {{ category.incomeTypeDescription }}
                        </option>
                      </select>
                      <button type="button" class="btn btn-outline-primary d-flex align-items-center gap-1"
                              @click="toggleChargesView" style="white-space: nowrap; min-width: 90px;">
                        <i class="bx bx-refresh"></i><span>Toggle</span>
                      </button>
                    </div>
                    <div class="d-flex align-items-center gap-2 mt-1">
                      <span class="badge" :class="showAllCharges ? 'bg-info' : 'bg-success'">
                        {{ showAllCharges ? 'Showing all categories' : 'Showing filtered' }}
                      </span>
                      <small class="text-muted">({{ otherChargeCategories.length }} available)</small>
                    </div>
                  </div>

                  <!-- Step 2 & 3: Select Specific Charge and Quantity -->
                  <div class="row g-2 mb-3" v-if="selectedOtherCategory && otherSubCategories.length > 0">
                    <div class="col-md-6">
                      <label class="form-label small fw-semibold">
                        <span class="badge bg-success me-1">Step 2</span> Select Specific Charge
                      </label>
                      <select v-model="selectedCharge" class="form-select">
                        <option :value="null">-- Select a charge --</option>
                        <option v-for="(charge, index) in otherSubCategories" :key="index" :value="charge">
                          {{ charge.feeDescription }} - KES {{ numberFormat(charge.unitFeeAmount) }}
                        </option>
                      </select>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label small fw-semibold">
                        <span class="badge bg-info me-1">Step 3</span> Quantity
                      </label>
                      <input v-model.number="chargeQuantity" type="number" min="1" class="form-control" placeholder="1">
                    </div>
                    <div class="col-md-3">
                      <label class="form-label small fw-semibold">&nbsp;</label>
                      <button type="button" class="btn btn-success w-100" @click="addChargeToList" :disabled="!selectedCharge">
                        <i class="bx bx-plus-circle me-1"></i> Add
                      </button>
                    </div>
                  </div>

                  <!-- Loading state for subcategories -->
                  <div class="alert alert-warning py-2" v-if="selectedOtherCategory && otherSubCategories.length === 0 && loadingSubCategories">
                    <i class="bx bx-loader bx-spin me-1"></i> Loading charges for {{ selectedOtherCategory.incomeTypeDescription }}...
                  </div>

                  <!-- Added Charges Table -->
                  <div class="table-responsive" v-if="addedCharges.length > 0">
                    <table class="table table-sm table-bordered mb-0">
                      <thead class="table-success">
                      <tr class="text-uppercase small">
                        <th>Charge Description</th>
                        <th width="70" class="text-center">Qty</th>
                        <th width="100" class="text-end">Unit Price</th>
                        <th width="100" class="text-end">Total</th>
                        <th width="50" class="text-center">Action</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(item, index) in addedCharges" :key="index">
                        <td class="small">{{ item.feeDescription }}</td>
                        <td class="text-center">{{ item.quantity }}</td>
                        <td class="text-end small">KES {{ numberFormat(item.unitFeeAmount) }}</td>
                        <td class="text-end fw-bold small">KES {{ numberFormat(item.unitFeeAmount * item.quantity) }}</td>
                        <td class="text-center">
                          <button type="button" class="btn btn-sm btn-outline-danger py-0 px-1" @click="removeCharge(index)">
                            <i class="bx bx-trash"></i>
                          </button>
                        </td>
                      </tr>
                      </tbody>
                      <tfoot class="table-info">
                      <tr>
                        <th colspan="3" class="text-end text-uppercase small">Total Additional Charges:</th>
                        <th class="text-end fw-bold">KES {{ numberFormat(totalAddedCharges) }}</th>
                        <th></th>
                      </tr>
                      </tfoot>
                    </table>
                  </div>

                  <!-- No charges info -->
                  <div class="alert alert-info py-2 mb-0" v-if="addedCharges.length === 0">
                    <i class="bx bx-info-circle me-1"></i>
                    <strong>No charges added yet.</strong> Select a category and charge above, then click "Add" to include it.
                  </div>
                </div>
                <!-- ==================== END OTHER APPLICABLE CHARGES ==================== -->

                <!-- Inspection Remarks -->
                <div class="mb-3">
                  <label class="form-label small fw-semibold">
                    Inspection Remarks <span class="text-danger">*</span>
                  </label>
                  <textarea v-model="comments" class="form-control" rows="3"
                            placeholder="Enter your inspection findings..."
                            :class="{ 'is-invalid': validationAttempted && !comments }"></textarea>
                </div>

                <!-- Computed Total Section -->
                <div class="computed-total-section p-3 rounded mb-3" v-if="selectedFeeAndCharge?.feeId">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <span class="text-muted small">License Fee:</span>
                    <span class="fw-semibold">KES {{ numberFormat(selectedFeeAndCharge.unitFeeAmount || 0) }}</span>
                  </div>
                  <div class="d-flex justify-content-between align-items-center mb-2" v-if="addedCharges.length > 0">
                    <span class="text-muted small">Other Charges:</span>
                    <span class="fw-semibold">KES {{ numberFormat(totalAddedCharges) }}</span>
                  </div>
                  <hr class="my-2">
                  <div class="d-flex justify-content-between align-items-center">
                    <span class="fw-bold">Total Amount:</span>
                    <span class="fw-bold text-success fs-5">KES {{ numberFormat(totalComputedAmount) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer bg-light border-0 py-3">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">
            <i class="bx bx-x me-1"></i> Cancel
          </button>
          <button @click="validateAndSubmit()" type="button" class="btn btn-primary" :disabled="isSubmitting">
            <span v-if="isSubmitting">
              <span class="spinner-border spinner-border-sm me-1"></span> Processing...
            </span>
            <span v-else>
              <i class="bx bx-check-circle me-1"></i> Approve for Next Phase
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { execute, biller, liquor } from "@/api";
import moment from "moment";

export default {
  name: "InspectionModal",
  props: {
    application: { type: Object, default: () => ({}) },
    licenseDurations: { type: Array, default: () => [] },
    operationalRequirements: { type: Array, default: () => [] }
  },
  emits: ['inspection-updated'],
  data() {
    return {
      comments: '',
      duration: '',
      selectedRequirements: [],
      otherRequirements: '',
      validationAttempted: false,
      isSubmitting: false,
      feesAndChargeItems: [],
      licenceCategories: [],
      licenceSubCategories: [],
      selectedIncomeType: {},
      selectedFeeAndCharge: {},

      // Other Applicable Charges
      otherChargeCategories: [],
      otherSubCategories: [],
      selectedOtherCategory: null,
      selectedCharge: null,
      chargeQuantity: 1,
      addedCharges: [],
      showAllCharges: false,
      loadingSubCategories: false
    }
  },
  computed: {
    totalAddedCharges() {
      return this.addedCharges.reduce((total, item) => total + (item.unitFeeAmount * item.quantity), 0);
    },
    totalComputedAmount() {
      const licenseFee = parseFloat(this.selectedFeeAndCharge?.unitFeeAmount) || 0;
      return licenseFee + this.totalAddedCharges;
    }
  },
  mounted() {
    this.getLicenceCategory();
    this.getOtherChargeCategories();
  },
  watch: {
    application: {
      handler() { this.resetForm(); },
      deep: true
    }
  },
  methods: {
    resetForm() {
      this.comments = '';
      this.duration = '';
      this.selectedRequirements = [];
      this.otherRequirements = '';
      this.validationAttempted = false;
      this.isSubmitting = false;
      this.selectedIncomeType = {};
      this.selectedFeeAndCharge = {};
      this.licenceSubCategories = [];

      // Reset other charges
      this.selectedOtherCategory = null;
      this.selectedCharge = null;
      this.chargeQuantity = 1;
      this.addedCharges = [];
      this.otherSubCategories = [];
    },

    numberFormat(num) {
      if (!num) return '0';
      return new Intl.NumberFormat("en-GB").format(num);
    },

    truncateText(text, max) {
      if (!text) return '';
      return text.length > max ? text.substring(0, max) + '...' : text;
    },

    formatDate(dateString) {
      return dateString ? moment(dateString).format('DD MMM YYYY') : '-';
    },

    getStatusClass(status) {
      const map = {
        'PAID': 'bg-success',
        'PENDING': 'bg-warning text-dark',
        'PAYMENT CLEARANCE': 'bg-primary'
      };
      return map[status?.toUpperCase()] || 'bg-secondary';
    },

    // License Category Methods
    getLicenceCategory() {
      const data = new FormData();
      data.append("function", "getIncomeTypes");
      data.append("incomeTypePrefix", "LIQUOR");
      execute(data, biller).then((res) => {
        if (res.data.success) this.licenceCategories = res.data.data.incomeTypes;
      });
    },

    getLicenceSubCategory(event) {
      if (!event.target.value) return;
      this.selectedIncomeType = JSON.parse(event.target.value);
      this.selectedFeeAndCharge = {};
      this.licenceSubCategories = [];

      const data = new FormData();
      data.append("function", "getFeesAndCharges");
      data.append("incomeTypeId", this.selectedIncomeType.incomeTypeId);
      execute(data, biller).then((res) => {
        if (res.data.success) this.licenceSubCategories = res.data.data.feesAndCharges;
      });
    },

    selectedLicenceSubCategory(event) {
      if (!event.target.value) return;
      const fee = JSON.parse(event.target.value);
      this.selectedFeeAndCharge = {
        ...fee,
        customerName: this.application.businessName,
        subCountyID: this.application.subCountyID,
        customer: `Applicant ID: ${this.application.businessID}`,
        subCountyName: this.application.subCountyName,
        wardName: this.application.wardName,
        wardID: this.application.wardID,
        idNo: sessionStorage.getItem("sessionIdNo"),
        phoneNumber: sessionStorage.getItem("sessionPhoneNumber"),
        names: sessionStorage.getItem("sessionNames"),
        amount: fee.unitFeeAmount
      };
      this.feesAndChargeItems = [fee];
    },

    selectedLicenseDurations(event) {
      this.duration = event.target.value;
    },

    // ==================== OTHER APPLICABLE CHARGES METHODS ====================
    getOtherChargeCategories(forceAll = false) {
      const data = new FormData();
      data.append("function", "getIncomeTypes");
      data.append("incomeTypePrefix", "LIQUOR");

      if (!forceAll && !this.showAllCharges) {
        data.append("keyword", "Other");
      }

      execute(data, biller).then((res) => {
        if (res.data.success && res.data.data.incomeTypes) {
          const incomeTypes = res.data.data.incomeTypes;

          if (incomeTypes.length === 0 && !forceAll) {
            this.showAllCharges = true;
            this.getOtherChargeCategories(true);
            return;
          }

          this.otherChargeCategories = incomeTypes;
        }
      });
    },

    toggleChargesView() {
      this.showAllCharges = !this.showAllCharges;
      this.getOtherChargeCategories(this.showAllCharges);
      this.selectedOtherCategory = null;
      this.otherSubCategories = [];
      this.selectedCharge = null;
    },

    handleOtherCategoryChange() {
      this.selectedCharge = null;
      this.otherSubCategories = [];

      if (this.selectedOtherCategory) {
        this.getOtherSubCategories(this.selectedOtherCategory.incomeTypeId);
      }
    },

    getOtherSubCategories(incomeTypeId) {
      this.loadingSubCategories = true;
      this.otherSubCategories = [];

      const data = new FormData();
      data.append("function", "getFeesAndCharges");
      data.append("incomeTypeId", incomeTypeId);

      execute(data, biller).then((res) => {
        this.loadingSubCategories = false;
        if (res.data.success) {
          this.otherSubCategories = res.data.data.feesAndCharges;
        }
      }).catch(() => {
        this.loadingSubCategories = false;
      });
    },

    addChargeToList() {
      if (!this.selectedCharge) return;

      const existingIndex = this.addedCharges.findIndex(item => item.feeId === this.selectedCharge.feeId);

      if (existingIndex !== -1) {
        // Update quantity if charge already exists
        this.addedCharges[existingIndex].quantity += this.chargeQuantity;
      } else {
        // Add new charge
        this.addedCharges.push({
          ...this.selectedCharge,
          quantity: this.chargeQuantity
        });
      }

      // Reset selection
      this.selectedCharge = null;
      this.chargeQuantity = 1;
    },

    removeCharge(index) {
      this.addedCharges.splice(index, 1);
    },
    // ==================== END OTHER APPLICABLE CHARGES METHODS ====================

    getSelectedRequirementNames() {
      const names = this.operationalRequirements
          .filter(r => this.selectedRequirements.includes(r.id))
          .map(r => r.requirement_name);
      if (this.otherRequirements?.trim()) names.push(`Other: ${this.otherRequirements.trim()}`);
      return names.join(', ');
    },

    validateAndSubmit() {
      this.validationAttempted = true;
      const errors = [];
      if (!this.duration) errors.push('License Duration');
      if (!this.selectedIncomeType?.incomeTypeId) errors.push('License Category');
      if (!this.selectedFeeAndCharge?.feeId) errors.push('License Sub Category');
      if (!this.comments?.trim()) errors.push('Inspection Remarks');

      if (errors.length) {
        alert(`Please fill: ${errors.join(', ')}`);
        return;
      }
      this.updateInspection();
    },

    updateInspection() {
      this.isSubmitting = true;

      // Start with the main license fee (this one gets the Applicant ID)
      const allFeesAndCharges = [];

      // Add main license fee with customer ID
      if (this.feesAndChargeItems.length > 0) {
        this.feesAndChargeItems.forEach(item => {
          allFeesAndCharges.push({
            ...item,
            customer: `Applicant ID: ${this.application.businessID}`,
            subCountyID: this.application.subCountyID,
            subCountyName: this.application.subCountyName,
            wardID: this.application.wardID,
            wardName: this.application.wardName,
            quantity: item.quantity || 1
          });
        });
      }

      // Add other applicable charges (these have empty customer field)
      this.addedCharges.forEach(charge => {
        allFeesAndCharges.push({
          ...charge,
          customer: '', // Leave blank for additional charges
          subCountyID: this.application.subCountyID,
          subCountyName: this.application.subCountyName,
          wardID: this.application.wardID,
          wardName: this.application.wardName
        });
      });

      const data = new FormData();
      data.append("function", "updateInspection");
      data.append("comments", this.comments);
      data.append("businessName", this.application.businessName);
      data.append("createdBy", sessionStorage.getItem("sessionNames"));
      data.append("idNo", sessionStorage.getItem("sessionIdNo"));
      data.append("billNo", this.application.billNo);
      data.append("licenceFeeID", this.selectedFeeAndCharge.feeId);
      data.append("licenceDuration", this.duration);
      data.append("licenceSubCategory", this.selectedFeeAndCharge.feeDescription);
      data.append("licenceCategory", this.selectedIncomeType.incomeTypeDescription);
      data.append("requirements", this.getSelectedRequirementNames());
      data.append("feesAndChargeItems", JSON.stringify(allFeesAndCharges));

      execute(data, liquor).then((res) => {
        this.isSubmitting = false;
        if (res.data.success) {
          alert('Inspection approved successfully!');
          this.$emit('inspection-updated');
          this.resetForm();
          const modal = document.querySelector('.receipting-modal');
          if (typeof bootstrap !== 'undefined') {
            bootstrap.Modal.getInstance(modal)?.hide();
          }
        } else {
          alert(res.data.message || 'Failed to update');
        }
      }).catch((e) => {
        this.isSubmitting = false;
        alert(e.message || 'Error occurred');
      });
    }
  }
}
</script>

<style scoped>
/* Modal Header */
.modal-icon {
  width: 45px;
  height: 45px;
  background: rgba(255,255,255,0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
}

/* Scrollable form area */
.modal-form-scroll {
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}

/* Info Section Styling */
.info-section {
  background: white;
  border-radius: 10px;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.9rem;
  color: #495057;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #f1f3f5;
}

.section-title i {
  font-size: 1.1rem;
}

.info-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0;
}

.info-item .label {
  color: #6c757d;
  font-size: 0.8rem;
}

.info-item .value {
  font-size: 0.85rem;
  text-align: right;
  max-width: 55%;
}

.info-item.highlight {
  background: #f8f9fa;
  padding: 0.5rem;
  border-radius: 6px;
  margin-top: 0.25rem;
}

/* Requirements Box */
.requirements-box {
  max-height: 140px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.75rem;
  background: #fafbfc;
}

.requirements-box .form-check {
  padding: 0.35rem 0;
  padding-left: 1.75rem;
}

.requirements-box::-webkit-scrollbar {
  width: 5px;
}

.requirements-box::-webkit-scrollbar-thumb {
  background: #dee2e6;
  border-radius: 3px;
}

/* Other Charges Section */
.other-charges-section {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 1rem;
  border: 1px dashed #dee2e6;
}

.other-charges-section .section-title {
  border-bottom: 2px solid #ffc107;
}

/* Computed Total Section */
.computed-total-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
}

/* Form Controls */
.form-select, .form-control {
  border-radius: 8px;
}

.form-select:focus, .form-control:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15);
}

/* Table styling */
.table-sm td, .table-sm th {
  padding: 0.4rem 0.5rem;
}

/* Scrollbar for modal form */
.modal-form-scroll::-webkit-scrollbar {
  width: 6px;
}

.modal-form-scroll::-webkit-scrollbar-thumb {
  background: #dee2e6;
  border-radius: 3px;
}

.modal-form-scroll::-webkit-scrollbar-thumb:hover {
  background: #adb5bd;
}
</style>